import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  TextField,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Facebook,
  Instagram,
  Email,
  Phone,
  LocationOn,
  Agriculture,
  Send,
} from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';
import { createNewsletter } from '../../graphql/mutations';
import { surface, brand, ink } from '../../theme/tokens';

// The footer sits on the dark brown panel (surface.dark), so every text colour
// here comes from the on-dark ramp: ink.onDark 9.48:1, ink.onDarkMuted 6.35:1,
// mustard 5.38:1. Each Typography states its colour explicitly rather than
// leaning on inheritance -- a variant colour or a `color=` prop beats an
// inherited one, which is how this footer ended up rendering brown-on-brown.
const headingSx = {
  display: 'inline-block',
  mb: 2,
  pb: 0.75,
  color: ink.onDark,
  fontWeight: 700,
  fontSize: '0.8125rem',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderBottom: `2px solid ${brand.mustard}`,
};

const linkSx = {
  color: ink.onDarkMuted,
  textDecoration: 'none',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: brand.mustard,
    textDecoration: 'underline',
  },
};

const client = generateClient();

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    
    try {
      await client.graphql({
        query: createNewsletter,
        variables: {
          input: {
            email: email.toLowerCase().trim(),
            subscribed: true
          }
        }
      });

      setSnackbar({
        open: true,
        message: 'Successfully subscribed to our newsletter!',
        severity: 'success'
      });
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      
      // Check if email already exists
      if (error.message && error.message.includes('duplicate')) {
        setSnackbar({
          open: true,
          message: 'This email is already subscribed to our newsletter',
          severity: 'info'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to subscribe. Please try again later.',
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: surface.dark,
        color: ink.onDark,
        borderTop: `4px solid ${brand.mustard}`,
        pt: { xs: 6, md: 8 },
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 5 }}>
          {/* Farm Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Agriculture sx={{ mr: 1.5, fontSize: 32, color: brand.mustard }} />
              <Typography
                variant="h5"
                sx={{ color: ink.onDark, fontWeight: 700, letterSpacing: '0.01em' }}
              >
                Vidhi Farm
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: ink.onDarkMuted, mb: 3, lineHeight: 1.8, maxWidth: 320 }}
            >
              Experience the finest organic produce, sustainable farming practices, 
              and peaceful farm life in the heart of West Bengal.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <IconButton
                href="https://www.facebook.com/share/17MbFMasdY/?mibextid=wwXIfr"
                target="_blank"
                aria-label="Vidhi Farm on Facebook"
                sx={{
                  color: ink.onDark,
                  border: `1px solid ${ink.onDarkMuted}`,
                  '&:hover': { color: brand.mustard, borderColor: brand.mustard },
                }}
              >
                <Facebook />
              </IconButton>
              <IconButton
                href="https://www.instagram.com/vidhi_permaculture_farm?igsh=MWJiYWtzbjUwOW1lbQ=="
                target="_blank"
                aria-label="Vidhi Farm on Instagram"
                sx={{
                  color: ink.onDark,
                  border: `1px solid ${ink.onDarkMuted}`,
                  '&:hover': { color: brand.mustard, borderColor: brand.mustard },
                }}
              >
                <Instagram />
              </IconButton>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" component="h2" sx={headingSx}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { label: 'Home', path: '/' },
                { label: 'Farm Stay', path: '/farm-stay' },
                { label: 'Products', path: '/products' },
                { label: 'Blog', path: '/blog' },
                { label: 'Gallery', path: '/gallery' },
                { label: 'Contact', path: '/contact' },
              ].map((link) => (
                <Typography
                  key={link.path}
                  component={Link}
                  to={link.path}
                  variant="body2"
                  sx={linkSx}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Products */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" component="h2" sx={headingSx}>
              Our Products
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                'Moringa Leaves',
                'Fresh Bananas',
                'Organic Mangoes',
                'Fresh Lemons',
                'Seasonal Vegetables',
                'Herbal Products',
              ].map((product) => (
                <Typography
                  key={product}
                  variant="body2"
                  sx={{ color: ink.onDarkMuted }}
                >
                  {product}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" component="h2" sx={headingSx}>
              Contact Info
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <LocationOn sx={{ fontSize: 20, mt: 0.2, color: brand.mustard }} />
                <Typography variant="body2" sx={{ color: ink.onDarkMuted }}>
                  Vidhi Farm, Birbhum
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Phone sx={{ fontSize: 20, color: brand.mustard }} />
                <Typography variant="body2" sx={{ color: ink.onDarkMuted }}>
                  +91 85830 79287
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Email sx={{ fontSize: 20, color: brand.mustard }} />
                <Typography variant="body2" sx={{ color: ink.onDarkMuted, wordBreak: 'break-word' }}>
                  ananya0066@gmail.com
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Newsletter */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" component="h2" sx={headingSx}>
              Stay Updated
            </Typography>
            <Typography variant="body2" sx={{ color: ink.onDarkMuted, mb: 2, lineHeight: 1.7 }}>
              Subscribe to our newsletter for the latest updates on fresh produce, 
              farm events, and seasonal offers.
            </Typography>
            <Box
              component="form"
              onSubmit={handleNewsletterSubmit}
              sx={{ display: 'flex', gap: 1 }}
            >
              <TextField
                size="small"
                placeholder="Your email address"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: surface.card,
                    color: ink.primary,
                  },
                  // MUI dims placeholders to 42% opacity, which lands well under
                  // AA. State the colour and keep it fully opaque.
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: ink.muted,
                    opacity: 1,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: brand.mustard,
                    borderWidth: 2,
                  },
                }}
              />
              <Button
                aria-label="Subscribe to the newsletter"
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  // mustard surface with the dark ink that is checked against it
                  background: brand.mustard,
                  color: ink.onMustard,
                  '&:hover': {
                    background: '#C0A20E',
                  },
                  minWidth: 48,
                  px: 2,
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mt: { xs: 5, md: 6 }, mb: 3, borderColor: 'rgba(242, 241, 236, 0.18)' }} />

        {/* Bottom Section */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: ink.onDarkMuted }}>
              © {new Date().getFullYear()} Vidhi Farm. All rights reserved.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                flexWrap: 'wrap',
              }}
            >
              <Typography
                component={Link}
                to="/privacy-policy"
                variant="body2"
                sx={linkSx}
              >
                Privacy Policy
              </Typography>
              <Typography
                component={Link}
                to="/terms-of-service"
                variant="body2"
                sx={linkSx}
              >
                Terms of Service
              </Typography>
              <Typography
                component={Link}
                to="/shipping-policy"
                variant="body2"
                sx={linkSx}
              >
                Shipping Policy
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Footer;